/**
 * Maps EV coverage UI models to Postgres update payloads for the coverage write API.
 *
 * @module coverageInfo/helpers/mapCoverageInformationToPostgresUpdate
 */

import moment from "moment";
import type {
  MiscellaneousAmountEntry,
  PatientResponsibilityEntry,
  SingleCoverageInformation,
} from "../../../data-model/coverageInformation";
import type { PatientResponsibility } from "../../../data-model/medicalPaOrdersModel";
import {
  CoverageMiscellaneousAmountType,
  CoveragePlanDisplayStatus,
  CoverageSubscriberType,
} from "../../../enums/coverageInformationEnums";
import { CoverageNetworkDisplay } from "../../../enums/coverageNetworkTypes";
import { CoverageStatus } from "../../../enums/coverageStatus";
import type { CoveragePostgresUpdateData } from "./coveragePostgresUpdateTypes";
import { bvFinancialValueToDbString } from "./evCoverageFinancialDisplay";
import { isUsableCoverageServiceType } from "./resolveCoverageServiceType";

/** Options controlling status fields when persisting manual edits. */
export interface MapCoverageInformationToPostgresUpdateOptions {
  /** EV screen status for the active insurance tier (drives row status on save). */
  evCoverageStatus: CoverageStatus;
  coverageType: string;
  /** Authenticated user email recorded as `coverage.updated_by`. */
  updatedBy: string;
}

/**
 * Moment format matching coverage read API timestamps
 * (e.g. `2021-10-01T00:00:00.000000+00:00`).
 */
const COVERAGE_SERVICE_YEAR_TIMESTAMP_FORMAT =
  "YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]";

/**
 * Converts a plan date from the EV UI into the ISO-8601 UTC timestamp string
 * stored on coverage rows (`service_year_from` / `service_year_to`).
 *
 * Date-only values from the native date input (`YYYY-MM-DD`) are expanded to
 * midnight UTC so the write path matches the full timestamp format returned by
 * the coverage read API.
 *
 * @param date - Effective/plan-end date from plan information (ISO or YYYY-MM-DD)
 * @returns Timestamp string for Postgres, or undefined when empty/invalid
 */
const toCoverageServiceYearTimestamp = (
  date: string | undefined,
): string | undefined => {
  const trimmed = date?.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = moment.utc(trimmed, ["YYYY-MM-DD", moment.ISO_8601], true);
  if (!parsed.isValid()) {
    return undefined;
  }
  return parsed.format(COVERAGE_SERVICE_YEAR_TIMESTAMP_FORMAT);
};

/**
 * Strips display formatting from copay / coinsurance fields before persistence.
 *
 * @param misc - Miscellaneous amount rows from the active treatment tab
 * @param type - Copay or co-insurance discriminator
 * @returns Raw string for the API payload
 */
const miscAmountToDbString = (
  misc: MiscellaneousAmountEntry[],
  type: CoverageMiscellaneousAmountType,
): string | undefined => {
  const entry = misc.find((m) => m.type === type);
  return bvFinancialValueToDbString(entry?.amount, undefined);
};

/**
 * Maps a deductible/OOP UI field back to a Postgres string.
 *
 * @param row - Deductible or OOP entry from the active treatment tab
 * @param amountField - Parsed numeric field name
 * @param rawField - Raw payer string field name
 * @returns Value for the patient-responsibility JSON block
 */
const subscriberAmountToDbString = <
  T extends {
    totalAmount?: number;
    usedAmount?: number;
    totalAmountRaw?: string;
    usedAmountRaw?: string;
  },
>(
  row: T | undefined,
  amountField: "totalAmount" | "usedAmount",
  rawField: "totalAmountRaw" | "usedAmountRaw",
): string | undefined =>
  bvFinancialValueToDbString(row?.[rawField], row?.[amountField]);

/**
 * Maps UI network display back to stored snake_case values.
 *
 * @param network - Display network type from the deductible/OOP row
 * @returns DB network type string
 */
const displayNetworkToDb = (
  network: CoverageNetworkDisplay | undefined,
): string | undefined => {
  switch (network) {
    case CoverageNetworkDisplay.IN_NETWORK:
      return "in_network";
    case CoverageNetworkDisplay.OUT_NETWORK:
      return "out_network";
    case CoverageNetworkDisplay.NO_NETWORK:
      return "no_network";
    case CoverageNetworkDisplay.NOT_APPLICABLE:
      return "not_applicable";
    default:
      return undefined;
  }
};

/**
 * Finds a deductible or OOP row for a subscriber tier.
 *
 * @param rows - Deductible or OOP entries for the active treatment tab
 * @param subscriberType - Individual or family bucket
 * @returns Matching row when present
 */
const findSubscriberRow = <
  T extends { subscriberType?: CoverageSubscriberType },
>(
  rows: T[],
  subscriberType: CoverageSubscriberType,
): T | undefined => rows.find((r) => r.subscriberType === subscriberType);

/**
 * Builds `patient_responsibility` JSON from the active treatment tab.
 *
 * @param entry - Patient responsibility tab entry from the UI model
 * @param coverageStatus - Plan active/inactive string for the JSON block
 * @returns PatientResponsibility object for the update API
 */
export const mapPatientResponsibilityToPostgres = (
  entry: PatientResponsibilityEntry | undefined,
  coverageStatus: string,
): PatientResponsibility | undefined => {
  if (!entry) {
    return undefined;
  }
  const pr = entry.singlePatientResponsibility;
  const serviceType = pr.serviceTypeInformation.serviceType?.trim() ?? "";
  const serviceTypeName =
    pr.serviceTypeInformation.serviceTypeDescription?.trim() ?? "";
  const deductibles = pr.deductibleAmountInformation ?? [];
  const outOfPocket = pr.outOfPocketAmountInformation ?? [];
  const misc = pr.miscellaneousAmountInformation ?? [];
  const individualDed = findSubscriberRow(
    deductibles,
    CoverageSubscriberType.INDIVIDUAL,
  );
  const familyDed = findSubscriberRow(
    deductibles,
    CoverageSubscriberType.FAMILY,
  );
  const individualOop = findSubscriberRow(
    outOfPocket,
    CoverageSubscriberType.INDIVIDUAL,
  );
  const familyOop = findSubscriberRow(
    outOfPocket,
    CoverageSubscriberType.FAMILY,
  );

  const payload: PatientResponsibility = {
    coverage_status: coverageStatus,
    ...(isUsableCoverageServiceType(serviceType)
      ? { service_type: serviceType }
      : {}),
    ...(serviceTypeName ? { service_type_name: serviceTypeName } : {}),
    individual_deductibles: subscriberAmountToDbString(
      individualDed,
      "totalAmount",
      "totalAmountRaw",
    ),
    individual_deductibles_met: subscriberAmountToDbString(
      individualDed,
      "usedAmount",
      "usedAmountRaw",
    ),
    family_deductibles: subscriberAmountToDbString(
      familyDed,
      "totalAmount",
      "totalAmountRaw",
    ),
    family_deductibles_met: subscriberAmountToDbString(
      familyDed,
      "usedAmount",
      "usedAmountRaw",
    ),
    individual_out_of_pocket: subscriberAmountToDbString(
      individualOop,
      "totalAmount",
      "totalAmountRaw",
    ),
    individual_out_of_pocket_met: subscriberAmountToDbString(
      individualOop,
      "usedAmount",
      "usedAmountRaw",
    ),
    family_out_of_pocket: subscriberAmountToDbString(
      familyOop,
      "totalAmount",
      "totalAmountRaw",
    ),
    family_out_of_pocket_met: subscriberAmountToDbString(
      familyOop,
      "usedAmount",
      "usedAmountRaw",
    ),
    co_pay: miscAmountToDbString(misc, CoverageMiscellaneousAmountType.CO_PAY),
    co_insurance: miscAmountToDbString(
      misc,
      CoverageMiscellaneousAmountType.CO_INSURANCE,
    ),
    deductibles_network_type: displayNetworkToDb(individualDed?.networkType),
    out_of_pocket_network_type: displayNetworkToDb(individualOop?.networkType),
  };

  const hasFinancialValue = Object.entries(payload).some(
    ([key, val]) =>
      key !== "coverage_status" && val !== undefined && val !== "",
  );
  if (!hasFinancialValue && !coverageStatus) {
    return undefined;
  }
  return payload;
};

/**
 * Resolves `coverage.status` when persisting manual edits.
 *
 * @param evCoverageStatus - Current EV flow status for the tier
 * @param planStatus - Active/inactive plan selection from the summary card
 * @returns {@link CoverageStatus} label to send, or undefined to leave unchanged
 */
export const resolveCoverageRowStatus = (
  evCoverageStatus: CoverageStatus,
  planStatus: CoveragePlanDisplayStatus,
): string | undefined => {
  if (
    evCoverageStatus === CoverageStatus.ERROR ||
    evCoverageStatus === CoverageStatus.EV_TYPE_NOT_SUPPORTED
  ) {
    return CoverageStatus.COMPLETED;
  }
  if (planStatus === CoveragePlanDisplayStatus.INACTIVE) {
    return CoverageStatus.INACTIVE;
  }
  return undefined;
};

/**
 * Maps plan display status to `coverage.coverage_status` / `active` columns.
 *
 * Active plans are written as `"Active Coverage"` to match the value EV/RPA
 * writes, which downstream services compare against verbatim.
 *
 * @param planStatus - Plan summary active/inactive selection
 * @returns {@link CoverageStatus} plan label and active flag for Postgres
 */
export const mapPlanStatusToPostgresFields = (
  planStatus: CoveragePlanDisplayStatus,
): { coverage_status: string; active: boolean } => {
  if (planStatus === CoveragePlanDisplayStatus.ACTIVE) {
    return { coverage_status: CoverageStatus.ACTIVE_COVERAGE, active: true };
  }
  if (planStatus === CoveragePlanDisplayStatus.INACTIVE) {
    return { coverage_status: CoverageStatus.INACTIVE, active: false };
  }
  return { coverage_status: "", active: false };
};

/**
 * Builds shared plan-level fields for every targeted coverage update request.
 *
 * @param snapshot - Edited coverage view model from the EV column
 * @param options - EV status and insurance tier type
 * @returns Plan columns and optional row status (without `patient_responsibility`)
 */
const buildSharedPlanFieldsForPostgresUpdate = (
  snapshot: SingleCoverageInformation,
  options: MapCoverageInformationToPostgresUpdateOptions,
): Omit<
  CoveragePostgresUpdateData,
  "patient_responsibility" | "service_type"
> => {
  const { planInformation, payerInformation } = snapshot;
  const { coverage_status: coverageStatusLabel, active } =
    mapPlanStatusToPostgresFields(planInformation.planStatus);
  const rowStatus = resolveCoverageRowStatus(
    options.evCoverageStatus,
    planInformation.planStatus,
  );
  const coverageType = options.coverageType.trim().toLowerCase();
  if (!coverageType) {
    throw new Error("coverage_type is required to persist coverage edits");
  }

  const updatedBy = options.updatedBy.trim();
  const data: Omit<
    CoveragePostgresUpdateData,
    "patient_responsibility" | "service_type"
  > = {
    coverage_type: coverageType,
    member_id: planInformation.memberId?.trim() || undefined,
    plan_name: planInformation.planName?.trim() || undefined,
    payer_name:
      payerInformation?.trim() || planInformation.planName?.trim() || undefined,
    service_year_from: toCoverageServiceYearTimestamp(
      planInformation.effectiveDate,
    ),
    service_year_to: toCoverageServiceYearTimestamp(
      planInformation.planEndDate,
    ),
    coverage_status: coverageStatusLabel,
    active,
    ...(updatedBy ? { updated_by: updatedBy } : {}),
  };

  if (rowStatus) {
    data.status = rowStatus;
  }

  return data;
};

/**
 * Builds one or more update payloads for POST /medical/frontend-query/update/coverage.
 *
 * Each payload targets a single Postgres row via `coverage_type` and, when present,
 * `service_type`. Plan-level fields are included on every request so all rows for
 * the tier stay aligned; `patient_responsibility` is scoped per treatment tab.
 *
 * @param snapshot - Edited coverage view model from the EV column
 * @param options - EV status and active insurance tier type
 * @returns One request `data` object per treatment tab (or one plan-only payload)
 */
export const mapCoverageInformationToPostgresUpdates = (
  snapshot: SingleCoverageInformation,
  options: MapCoverageInformationToPostgresUpdateOptions,
): CoveragePostgresUpdateData[] => {
  const shared = buildSharedPlanFieldsForPostgresUpdate(snapshot, options);
  const { coverage_status: coverageStatusLabel } =
    mapPlanStatusToPostgresFields(snapshot.planInformation.planStatus);
  const prEntries = snapshot.patientResponsibilityInformation ?? [];

  if (prEntries.length === 0) {
    return [shared];
  }

  return prEntries.map((entry) => {
    const serviceType =
      entry.singlePatientResponsibility.serviceTypeInformation.serviceType?.trim() ??
      "";
    if (!isUsableCoverageServiceType(serviceType)) {
      throw new Error(
        "Each treatment tab must have a service type before saving coverage.",
      );
    }
    const patientResponsibility = mapPatientResponsibilityToPostgres(
      entry,
      coverageStatusLabel,
    );
    const payload: CoveragePostgresUpdateData = {
      ...shared,
      service_type: serviceType,
      patient_responsibility: patientResponsibility,
    };
    return payload;
  });
};
